import React from 'react';
import { createRoot } from 'react-dom/client';

/**
 * Shared behavior for Claudia microfrontend wrappers.
 *
 * Components using this mixin must provide:
 *   - remoteName (string)      — human-readable label for the error fallback
 *   - rootRef (string)         — name of the template ref hosting the React root
 *   - componentProps (computed) — props forwarded to the remote component
 *   - loadRemote() (method)    — resolves with the remote module (default or namespace)
 *
 * Responsibilities:
 *   - Resolves tenant before mounting the remote (boot ordering).
 *   - Exposes tenantLoading/tenantError so the template can render feedback.
 *   - Cancels stale tenant requests when accountId changes.
 *   - Re-renders the remote when any input prop changes (deep watch).
 *   - Guards $refs / createRoot against access after component destruction.
 */
export default {
  data() {
    return {
      tenantName: null,
      tenantProjects: [],
      eddieWorkspaces: [],
      tenantLoading: true,
      tenantError: null,
    };
  },
  watch: {
    '$route.params.accountId': {
      immediate: true,
      handler: 'fetchTenant',
    },
    componentProps: {
      deep: true,
      handler() {
        this.renderRemote();
      },
    },
  },
  beforeCreate() {
    // Must be beforeCreate: watchers with `immediate: true` fire during
    // initState (before `created`), so initializing these in `created` would
    // leave `_mfFetchToken` as `undefined` on the first fetchTenant call —
    // `NaN !== NaN` would then short-circuit the try/catch/finally and leak
    // `tenantLoading=true` forever on a failed request.
    // Fields are namespaced (`_mf*`) to avoid clashing with Vue 2 internals
    // like `vm._isDestroyed`, `vm._isMounted`, etc.
    this._mfDestroyed = false;
    this._mfFetchToken = 0;
    this._mfComponent = null;
    this._mfRoot = null;
  },
  beforeDestroy() {
    this._mfDestroyed = true;
    this.unmountRoot();
  },
  methods: {
    async fetchTenant() {
      const accountId = this.$route.params.accountId;
      if (!accountId) return;

      const token = ++this._mfFetchToken;
      this.tenantLoading = true;
      this.tenantError = null;

      try {
        const { data } = await window.axios.get(
          `/api/v1/accounts/${accountId}/tenant`
        );
        if (this._mfDestroyed || token !== this._mfFetchToken) return;
        this.tenantName = data?.tenantName || null;
        this.tenantProjects = Array.isArray(data?.tenantProjects)
          ? data.tenantProjects
          : [];
        this.eddieWorkspaces = Array.isArray(data?.eddie_workspaces)
          ? data.eddie_workspaces
              .filter(ws => ws != null)
              .map(ws => ({
                id: ws.id,
                name: ws.name,
                instance: ws.instance,
              }))
          : [];
      } catch (error) {
        if (this._mfDestroyed || token !== this._mfFetchToken) return;
        console.error('Failed to fetch tenant:', error);
        this.tenantName = null;
        this.tenantProjects = [];
        this.eddieWorkspaces = [];
        this.tenantError = `The ${this.remoteName} microfrontend is currently unavailable.`;
      } finally {
        if (this._mfDestroyed || token !== this._mfFetchToken) return;
        this.tenantLoading = false;
        if (!this.tenantError) {
          this.renderRemote();
        }
      }
    },
    async ensureRemoteLoaded() {
      if (this._mfComponent) return;
      const ComponentModule = await this.loadRemote();
      if (this._mfDestroyed) return;
      this._mfComponent = ComponentModule.default || ComponentModule;
    },
    async renderRemote() {
      if (this._mfDestroyed) return;
      if (this.tenantLoading || this.tenantError) return;

      try {
        await this.ensureRemoteLoaded();
        if (this._mfDestroyed || !this._mfComponent) return;

        await this.$nextTick();
        if (this._mfDestroyed) return;

        const container = this.$refs[this.rootRef];
        if (!container) return;

        if (!this._mfRoot) {
          this._mfRoot = createRoot(container);
        }
        this._mfRoot.render(
          React.createElement(this._mfComponent, this.componentProps)
        );
      } catch (error) {
        if (this._mfDestroyed) return;
        console.error(
          `Error loading ${this.remoteName} microfrontend:`,
          error
        );
        this.unmountRoot();
        this.tenantError = `The ${this.remoteName} microfrontend is currently unavailable.`;
      }
    },
    unmountRoot() {
      if (!this._mfRoot) return;
      try {
        this._mfRoot.unmount();
      } catch (error) {
        // Unmount can throw if the container was already detached.
      }
      this._mfRoot = null;
    },
  },
};
